import { useFeather } from '../shell/useFeather';
import { useState } from 'react';
import { forgetMailingSubscription, MailingSubscriptionBadge } from '../mailing/MailingSubscriptionBadge';
import { MailingGroupPicker } from '../mailing/MailingGroupPicker';
import type { MailingSpace } from '../api/mailing';
import { Button } from '../components/arc/button/button';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import './affilmaster-contact-directory.css';

export interface AffilmasterContactDirectoryItem {
  id: number | string;
  name: string;
  role: string;
  vocative?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface AffilmasterContactDirectoryProps {
  contacts: AffilmasterContactDirectoryItem[];
  label: string;
  onEdit?: (contact: AffilmasterContactDirectoryItem) => void;
  onDelete?: (contact: AffilmasterContactDirectoryItem) => void;
  mailingSpace?: MailingSpace | null;
}

function contactInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase() || '—';
}

export function AffilmasterContactDirectory({ contacts, label, onEdit, onDelete, mailingSpace = 'affilmaster' }: AffilmasterContactDirectoryProps) {
  const hasActions = Boolean(onEdit || onDelete);
  const [badgeVersion, setBadgeVersion] = useState(0);
  useFeather([contacts.length, hasActions]);

  return (
    <ArcPanel as="div" className={`am-contact-directory${hasActions ? '' : ' is-read-only'}`} role="table" aria-label={label}>
      <div className="am-contact-directory-head" role="row">
        <span role="columnheader">Kontakt</span>
        <span role="columnheader">Spojení</span>
        <span role="columnheader">{mailingSpace ? 'Mailing a skupiny' : ''}</span>
        {hasActions && <span role="columnheader" className="visually-hidden">Akce</span>}
      </div>
      <div className="am-contact-directory-body" role="rowgroup">
        {contacts.map((contact, index) => {
          const email = contact.email?.trim() || null;
          const phone = contact.phone?.trim() || null;
          return (
            <article className="am-contact-directory-row" role="row" key={contact.id}>
              <div className="am-contact-directory-person" role="cell">
                <span className={`am-contact-directory-avatar${index % 2 ? ' alt' : ''}`} aria-hidden="true">
                  {contactInitials(contact.name)}
                </span>
                <div>
                  <strong>{contact.name}</strong>
                  <span className="am-contact-directory-role">{contact.role}</span>
                  {contact.vocative && <small>Oslovení: <b>{contact.vocative}</b></small>}
                </div>
              </div>

              <div className="am-contact-directory-channels" role="cell" data-label="Spojení">
                {email && <a href={`mailto:${email}`} title={`Napsat na ${email}`}><i data-feather="mail" />{email}</a>}
                {phone && <a href={`tel:${phone.replace(/\s+/g, '')}`} title={`Zavolat na ${phone}`}><i data-feather="phone" />{phone}</a>}
                {!email && !phone && <span className="am-contact-directory-empty">Bez kontaktních údajů</span>}
              </div>

              <div className="am-contact-directory-tags" role="cell" data-label="Mailing a skupiny">
                {mailingSpace && (email ? <MailingSubscriptionBadge key={`${email}-${badgeVersion}`} email={email} space={mailingSpace} /> : <span className="am-contact-directory-empty">Bez e-mailu</span>)}
                {mailingSpace && email && hasActions && <MailingGroupPicker email={email} space={mailingSpace} onChange={() => { forgetMailingSubscription(email, mailingSpace); setBadgeVersion((v) => v + 1); }} />}
              </div>

              {hasActions && (
                <div className="am-contact-directory-actions" role="cell">
                  {onEdit && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(contact)} title={`Upravit ${contact.name}`} aria-label={`Upravit ${contact.name}`}>
                      <i data-feather="edit-2" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button type="button" variant="ghost" size="sm" className="is-danger" onClick={() => onDelete(contact)} title={`Smazat ${contact.name}`} aria-label={`Smazat ${contact.name}`}>
                      <i data-feather="trash-2" />
                    </Button>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </ArcPanel>
  );
}
